import { z } from "zod";
import {
  createBackgroundFunction,
  createFunction,
  createPublicFunction,
} from "~/api";
import * as types from "~/types";

export const deletePhotoSchema = {
  name: "deletePhoto",
  input: z.object({
    id: z.string(),
  }),
  output: z.object({}),
};

export const deletePhoto = createFunction(deletePhotoSchema);

export const listNutritionsSchema = {
  name: "listNutritions",
  input: z.object({
    username: z.string().optional(),
  }),
  output: z.object({
    nutritions: z.array(types.NutritionExpandedSchema),
  }),
};

export const listNutritions = createPublicFunction(listNutritionsSchema);

export const getNutritionSchema = {
  name: "getNutrition",
  input: z.object({
    username: z.string().optional(),
    nutritionId: z.string(),
  }),
  output: z.object({
    nutrition: z.union([types.NutritionExpandedSchema, z.null()]),
  }),
};

export const getNutrition = createPublicFunction(getNutritionSchema);

export const deleteNutritionSchema = {
  name: "deleteNutrition",
  input: z.object({
    nutritionId: z.string(),
  }),
  output: z.object({}),
};

export const deleteNutrition = createFunction(deleteNutritionSchema);

export const listGuidesSchema = {
  name: "listGuides",
  input: z.object({}),
  output: z.object({
    guides: z.array(types.GuideSchema),
  }),
};

export const listGuides = createPublicFunction(listGuidesSchema);

export const getGuideSchema = {
  name: "getGuide",
  input: z.object({ nameId: z.string() }),
  output: z.object({
    guide: types.GuideSchema,
  }),
};

export const getGuide = createPublicFunction(getGuideSchema);

export const createTeamSchema = {
  name: "createTeam",
  input: z.object({
    name: z.string(),
    username: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    team: z.union([types.TeamSchema, z.null()]),
  }),
};

export const createTeam = createFunction(createTeamSchema);

export const updateTeamSchema = {
  name: "updateTeam",
  input: z.object({
    updateData: types.TeamSchema.pick({
      name: true,
      location: true,
      sport: true,
      overviewText: true,
      overviewContent: true,
      overviewHtml: true,
      twitter: true,
      youtube: true,
      instagram: true,
      tiktok: true,
      facebook: true,
    }).partial(),
  }),
  output: z.object({
    team: types.TeamSchema,
  }),
};

export const updateTeam = createFunction(updateTeamSchema);

export const updateTeamUsernameSchema = {
  name: "updateTeamUsername",
  input: z.object({
    username: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    team: z.union([types.TeamSchema, z.null()]),
  }),
};

export const updateTeamUsername = createFunction(updateTeamUsernameSchema);

export const getTeamSchema = {
  name: "getTeam",
  input: z.object({
    username: z.string().optional(),
  }),
  output: z.object({
    team: z.union([types.TeamSchema, z.null()]),
  }),
};

export const getTeam = createPublicFunction(getTeamSchema);

export const listTeamsSchema = {
  name: "listTeams",
  input: z.object({}),
  output: z.object({
    teams: z.array(types.TeamSchema),
  }),
};

export const listTeams = createPublicFunction(listTeamsSchema);

export const deleteTeamSchema = {
  name: "deleteTeam",
  input: z.object({}),
  output: z.object({}),
};

export const deleteTeam = createFunction(deleteTeamSchema);

export const addToTeamSchema = {
  name: "addToTeam",
  input: z.object({
    profileId: z.string(),
    type: z.enum(["athlete", "staff"]),
  }),
  output: z.object({}),
};

export const addToTeam = createFunction(addToTeamSchema);

export const listTeamMembersSchema = {
  name: "listTeamMembers",
  input: z.object({
    username: z.string(),
    type: types.TeamMemberTypeSchema,
  }),
  output: z.object({
    teamMembers: z.array(types.TeamMemberSchema),
  }),
};

export const listTeamMembers = createPublicFunction(listTeamMembersSchema);

export const getTeamMemberSchema = {
  name: "getTeamMember",
  input: z.object({
    teamMemberId: z.string(),
    type: types.TeamMemberTypeSchema,
  }),
  output: z.object({
    teamMember: z.union([types.TeamMemberSchema, z.null()]),
  }),
};

export const getTeamMember = createFunction(getTeamMemberSchema);

export const updateTeamMemberSchema = {
  name: "updateTeamMember",
  input: z.object({
    teamMemberId: z.string(),
    updateData: types.TeamMemberSchema.pick({
      tags: true,
    }).partial(),
  }),
  output: z.object({}),
};

export const updateTeamMember = createFunction(updateTeamMemberSchema);

export const removeFromTeamSchema = {
  name: "removeFromTeam",
  input: z.object({
    profileId: z.string(),
    type: types.TeamMemberTypeSchema,
  }),
  output: z.object({}),
};

export const removeFromTeam = createFunction(removeFromTeamSchema);

export const deleteTeamLogoSchema = {
  name: "deleteTeamLogo",
  input: z.object({}),
  output: z.object({}),
};

export const deleteTeamLogo = createFunction(deleteTeamLogoSchema);

export const createTeamShareSchema = {
  name: "createTeamShare",
  input: z.object({
    email: z.string(),
    messageText: z.union([z.string(), z.null()]),
    messageHtml: z.union([z.string(), z.null()]),
    messageContent: z.any(),
    positions: z.array(z.string()),
    tags: z.array(z.string()),
  }),
  output: z.object({
    teamShare: types.TeamShareSchema,
  }),
};

export const createTeamShare = createFunction(createTeamShareSchema);

export const getTeamShareSchema = {
  name: "getTeamShare",
  input: z.object({
    teamShareId: z.string(),
  }),
  output: z.object({
    teamShare: z.union([types.TeamShareSchema, z.null()]),
  }),
};

export const getTeamShare = createPublicFunction(getTeamShareSchema);

export const getFaiTokenSchema = {
  name: "getFaiToken",
  input: z.object({
    username: z.union([z.string(), z.null()]).optional(),
  }),
  output: z.object({
    token: z.string(),
  }),
};

export const getFaiToken = createFunction(getFaiTokenSchema);

export const createMessageSchema = {
  name: "createMessage",
  input: z.object({
    tempId: z.string(),
    toProfileId: z.string(),
    text: z.string(),
    textContent: z.any().optional(),
    textHtml: z.union([z.string(), z.null()]).optional(),
  }),
  output: z.object({
    message: types.MessageSchema,
  }),
};

export const createMessage = createFunction(createMessageSchema);

export const listMessagesSchema = {
  name: "listMessages",
  input: z.object({
    toProfileId: z.string(),
    limit: z.number().optional(),
  }),
  output: z.object({
    messages: z.array(types.MessageExpandedSchema),
  }),
};

export const listMessages = createFunction(listMessagesSchema);

export const listMessageProfilesSchema = {
  name: "listMessageProfiles",
  input: z.object({}),
  output: z.object({
    profiles: z.array(types.ProfileSchema),
  }),
};

export const listMessageProfiles = createFunction(listMessageProfilesSchema);

export const deleteMessageSchema = {
  name: "deleteMessage",
  input: z.object({
    messageId: z.string(),
  }),
  output: z.object({}),
};

export const deleteMessage = createFunction(deleteMessageSchema);

export const getProfileSchema = {
  name: "getProfile",
  input: z.object({
    username: z.string(),
  }),
  output: z.object({
    profile: z.union([types.ProfileExpandedSchema, z.null()]),
    team: z.union([types.TeamSchema, z.null()]),
  }),
};

export const getProfile = createPublicFunction(getProfileSchema);

export const listUserFollowingSchema = {
  name: "listUserFollowing",
  input: z.object({}),
  output: z.object({
    profiles: z.array(types.ProfileExpandedSchema),
  }),
};

export const listUserFollowing = createFunction(listUserFollowingSchema);

export const getUserProfileSchema = {
  name: "getUserProfile",
  input: z.object({}),
  output: z.object({
    profile: z.union([types.ProfileExpandedSchema, z.null()]),
  }),
};

export const getUserProfile = createFunction(getUserProfileSchema);

export const updateProfileNameSchema = {
  name: "updateProfileName",
  input: z.object({
    name: z.string().min(1),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    profile: z.union([types.ProfileSchema, z.null()]),
  }),
};

export const updateProfileName = createFunction(updateProfileNameSchema);

export const updateProfileUsernameSchema = {
  name: "updateProfileUsername",
  input: z.object({
    username: z.string().min(1),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    profile: z.union([types.ProfileExpandedSchema, z.null()]),
  }),
};

export const updateProfileUsername = createFunction(
  updateProfileUsernameSchema,
);

export const updateProfileSchema = {
  name: "updateProfile",
  input: z.object({
    updateData: types.ProfileExpandedSchema.pick({
      isPublic: true,
      location: true,
      sport: true,
      position: true,
      position2: true,
      sportEvent: true,
      team: true,
      kitNumber: true,
      dateOfBirth: true,
      height: true,
      weight: true,
      twitter: true,
      youtube: true,
      instagram: true,
      tiktok: true,
      facebook: true,
      gpa: true,
      classYear: true,
      collegeOffersText: true,
      collegeOffersContent: true,
      collegeOffersHtml: true,
      fortyYardDash: true,
      verticalJump: true,
      broadJump: true,
      proAgility: true,
      gutCheck: true,
      benchPress: true,
      squat: true,
      powerClean: true,
    }).partial(),
  }),
  output: z.object({
    profile: types.ProfileExpandedSchema,
  }),
};

export const updateProfile = createFunction(updateProfileSchema);

export const updateProfileHudlUrlSchema = {
  name: "updateProfileHudlUrl",
  input: z.object({
    hudlUrl: z.string(),
  }),
  output: z.object({
    profile: types.ProfileExpandedSchema,
  }),
};

export const updateProfileHudlUrl = createFunction(updateProfileHudlUrlSchema);

export const listProfilePhotosSchema = {
  name: "listProfilePhotos",
  input: z.object({
    username: z.string().optional(),
  }),
  output: z.object({
    photos: z.array(types.PhotoSchema),
  }),
};

export const listProfilePhotos = createPublicFunction(listProfilePhotosSchema);

export const updateProfileOverviewSchema = {
  name: "updateProfileOverview",
  input: z.object({
    overviewContent: z.any(),
    overviewHtml: z.union([z.string(), z.null()]),
    overviewText: z.union([z.string(), z.null()]),
    collegeOffersContent: z.any(),
    collegeOffersHtml: z.union([z.string(), z.null()]),
    collegeOffersText: z.union([z.string(), z.null()]),
  }),
  output: z.object({}),
};

export const updateProfileOverview = createFunction(
  updateProfileOverviewSchema,
);

export const updateProfileHighlightsSchema = {
  name: "updateProfileHighlights",
  input: z.object({
    highlights: z.any(),
  }),
  output: z.object({}),
};

export const updateProfileHighlights = createFunction(
  updateProfileHighlightsSchema,
);

export const followSchema = {
  name: "follow",
  input: z.object({
    username: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    profile: z.union([types.ProfileExpandedSchema, z.null()]),
  }),
};

export const follow = createFunction(followSchema);

export const unfollowSchema = {
  name: "unfollow",
  input: z.object({
    username: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    profile: z.union([types.ProfileExpandedSchema, z.null()]),
  }),
};

export const unfollow = createFunction(unfollowSchema);

export const getFollowRequestSchema = {
  name: "getFollowRequest",
  input: z.object({
    followRequestId: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    fromProfile: z.union([types.ProfileExpandedSchema, z.null()]),
    toProfile: z.union([types.ProfileExpandedSchema, z.null()]),
  }),
};

export const getFollowRequest = createPublicFunction(getFollowRequestSchema);

export const approveFollowRequestSchema = {
  name: "approveFollowRequest",
  input: z.object({
    followRequestId: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
  }),
};

export const approveFollowRequest = createPublicFunction(
  approveFollowRequestSchema,
);

export const processProfileHudlVideosSchema = {
  name: "processProfileHudlVideos",
  input: z.object({
    userId: z.string(),
  }),
};

export const processProfileHudlVideos = createBackgroundFunction(
  processProfileHudlVideosSchema,
);

export const deleteProfilePhotoSchema = {
  name: "deleteProfilePhoto",
  input: z.object({}),
  output: z.object({}),
};

export const deleteProfilePhoto = createFunction(deleteProfilePhotoSchema);

export const listLogsBestSchema = {
  name: "listLogsBest",
  input: z.object({
    username: z.string().optional(),
    category: z.string().optional(),
  }),
  output: z.object({
    logs: z.array(types.LogWithActivityTypeSchema),
  }),
};

export const listLogsBest = createPublicFunction(listLogsBestSchema);

export const getLogBestSchema = {
  name: "getLogBest",
  input: z.object({
    username: z.string().optional(),
    activityTypeNameId: z.string(),
  }),
  output: z.object({
    log: z.union([types.LogWithActivityTypeSchema, z.null()]),
  }),
};

export const getLogBest = createPublicFunction(getLogBestSchema);

export const listLogsSchema = {
  name: "listLogs",
  input: z.object({
    username: z.string().optional(),
    startDate: z.number(),
    endDate: z.number(),
    category: z.string(),
    activityTypeNameId: z.string(),
    type: z.string().nullable().optional(),
    limit: z.number().optional(),
  }),
  output: z.object({
    logs: z.array(types.LogSchema),
  }),
};

export const listLogs = createPublicFunction(listLogsSchema);

export const deleteLogSchema = {
  name: "deleteLog",
  input: z.object({
    logId: z.string(),
  }),
  output: z.object({}),
};

export const deleteLog = createFunction(deleteLogSchema);

export const addRawHealthDataSchema = {
  name: "addRawHealthData",
  input: z.object({
    type: types.HealthTypeSchema,
    dateId: z.string(),
    data: z.array(z.unknown()),
  }),
  output: z.object({}),
};

export const addRawHealthData = createFunction(addRawHealthDataSchema);

export const addPushDeviceSchema = {
  name: "addPushDevice",
  input: z.object({
    token: z.string(),
  }),
  output: z.object({}),
};

export const addPushDevice = createFunction(addPushDeviceSchema);

export const createPostSchema = {
  name: "createPost",
  input: z.object({
    date: z.number(),
    title: z.string().optional(),
    content: z.any().optional(),
    html: z.union([z.string(), z.null()]).optional(),
    text: z.union([z.string(), z.null()]).optional(),
    photos: z.array(types.PhotoSchema).optional(),
  }),
  output: z.object({
    post: types.PostSchema,
  }),
};

export const createPost = createFunction(createPostSchema);

export const updatePostSchema = {
  name: "updatePost",
  input: z.object({
    postId: z.string(),
    date: z.number().optional(),
    title: z.string().optional(),
    content: z.any().optional(),
    html: z.union([z.string(), z.null()]).optional(),
    text: z.union([z.string(), z.null()]).optional(),
    photos: z.array(types.PhotoSchema).optional(),
  }),
  output: z.object({
    post: types.PostSchema,
  }),
};

export const updatePost = createFunction(updatePostSchema);

export const deletePostSchema = {
  name: "deletePost",
  input: z.object({
    postId: z.string(),
  }),
  output: z.object({}),
};

export const deletePost = createFunction(deletePostSchema);

export const listPostsSchema = {
  name: "listPosts",
  input: z.object({
    username: z.string().optional(),
    limit: z.number().optional(),
  }),
  output: z.object({
    posts: z.array(types.PostSchema),
  }),
};

export const listPosts = createPublicFunction(listPostsSchema);

export const getPostSchema = {
  name: "getPost",
  input: z.object({
    username: z.string().optional(),
    postId: z.string(),
  }),
  output: z.object({
    post: z.union([types.PostSchema, z.null()]),
  }),
};

export const getPost = createPublicFunction(getPostSchema);

export const listOnboardingStepsSchema = {
  name: "listOnboardingSteps",
  input: z.object({}),
  output: z.object({
    steps: z.array(types.OnboardingStepSchema),
  }),
};

export const listOnboardingSteps = createPublicFunction(
  listOnboardingStepsSchema,
);

export const createVideoSchema = {
  name: "createVideo",
  input: z.object({
    key: z.string(),
    url: z.string().url(),
  }),
  output: z.object({
    video: types.VideoSchema,
  }),
};

export const createVideo = createFunction(createVideoSchema);

export const listProfileVideosSchema = {
  name: "listProfileVideos",
  input: z.object({
    username: z.string().optional(),
  }),
  output: z.object({
    videos: z.array(types.VideoSchema),
  }),
};

export const listProfileVideos = createPublicFunction(listProfileVideosSchema);

export const removeVideoSchema = {
  name: "removeVideo",
  input: z.object({
    videoId: z.string(),
  }),
  output: z.object({}),
};

export const removeVideo = createFunction(removeVideoSchema);

export const processVideoUploadSchema = {
  name: "processVideoUpload",
  input: z.object({
    userId: z.string(),
    videoId: z.string(),
    url: z.string(),
  }),
};

export const processVideoUpload = createBackgroundFunction(
  processVideoUploadSchema,
);

export const getUserSchema = {
  name: "getUser",
  input: z.object({}),
  output: z.object({
    user: z.union([types.UserSchema, z.null()]),
  }),
};

export const getUser = createFunction(getUserSchema);

export const signUpSchema = {
  name: "signUp",
  input: z.object({
    name: z.string(),
    email: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    ok: z.boolean(),
  }),
};

export const signUp = createPublicFunction(signUpSchema);

export const signInSchema = {
  name: "signIn",
  input: z.object({
    email: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
  }),
};

export const signIn = createPublicFunction(signInSchema);

export const verifyAuthCodeSchema = {
  name: "verifyAuthCode",
  input: z.object({
    source: z.enum(["web", "app"]).optional(),
    code: z.string(),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    authToken: z.union([z.string(), z.null()]),
    user: z.union([types.UserSchema, z.null()]),
  }),
};

export const verifyAuthCode = createPublicFunction(verifyAuthCodeSchema);

export const googleAuthFromProfileSchema = {
  name: "googleAuthFromProfile",
  input: z.object({
    source: z.enum(["web", "app"]).optional(),
    accessToken: z.string(),
    profile: z.object({
      name: z.union([z.string(), z.null()]),
    }),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    authToken: z.union([z.string(), z.null()]),
    user: z.union([types.UserSchema, z.null()]),
  }),
};

export const googleAuthFromProfile = createPublicFunction(
  googleAuthFromProfileSchema,
);

export const appleAuthFromProfileSchema = {
  name: "appleAuthFromProfile",
  input: z.object({
    source: z.enum(["web", "app"]).optional(),
    accessToken: z.string(),
    profile: z.object({
      name: z.union([z.string(), z.null()]),
    }),
  }),
  output: z.object({
    error: z.union([z.string(), z.null()]),
    authToken: z.union([z.string(), z.null()]),
    user: z.union([types.UserSchema, z.null()]),
  }),
};

export const appleAuthFromProfile = createPublicFunction(
  appleAuthFromProfileSchema,
);

export const deleteAccountSchema = {
  name: "deleteAccount",
  input: z.object({}),
  output: z.object({}),
};

export const deleteAccount = createFunction(deleteAccountSchema);

export const createContractSchema = {
  name: "createContract",
  input: z.object({}),
  output: z.object({
    contract: types.ContractSchema,
  }),
};

export const createContract = createFunction(createContractSchema);

export const updateContractSchema = {
  name: "updateContract",
  input: z.object({
    contractId: z.string(),
    startDate: z.number(),
    endDate: z.number(),
    year: z.number(),
    data: z.any(),
  }),
  output: z.object({
    contract: types.ContractSchema,
  }),
};

export const updateContract = createFunction(updateContractSchema);

export const deleteContractSchema = {
  name: "deleteContract",
  input: z.object({
    contractId: z.string(),
  }),
  output: z.object({}),
};

export const deleteContract = createFunction(deleteContractSchema);

export const listContractsSchema = {
  name: "listContracts",
  input: z.object({
    username: z.string().optional(),
    limit: z.number().optional(),
  }),
  output: z.object({
    contracts: z.array(types.ContractSchema),
  }),
};

export const listContracts = createPublicFunction(listContractsSchema);

export const getContractSchema = {
  name: "getContract",
  input: z.object({
    username: z.string().optional(),
    contractId: z.string(),
  }),
  output: z.object({
    contract: z.union([types.ContractSchema, z.null()]),
  }),
};

export const getContract = createPublicFunction(getContractSchema);

export const adminSchema = {
  name: "admin",
  input: z.object({}),
  output: z.object({}),
};

export const admin = createPublicFunction(adminSchema);

export const emptyBackgroundSchema = {
  name: "emptyBackground",
  input: z.object({}),
};

export const emptyBackground = createBackgroundFunction(emptyBackgroundSchema);
